import {
    filter,
    map,
    concatMap,
    Observable,
    share,
    Subscription,
} from 'rxjs';

import OfferEvent from '@core/models/offer/OfferEvent';
import Struct from '@core/models/struct/Struct';

import { DailyOfferSseConnection } from './connections/DailyOfferSseConnection';
import { LiveEventsCountConnection } from './connections/LiveEventsCountConnection';
import { MatchDetailsSseConnection } from './connections/MatchDetailsSseConnection';
import { StructSseConnection } from './connections/StructSseConnection';
import {
    EventStream,
    OutOfSyncUpdate,
    SseStructure,
    SseUpdate,
} from './types';
import { StructManager } from '../../StructManager';
import { StreamManager } from '../StreamManager';

export class SseManager implements StreamManager {
    private dailyOfferSseConnection: DailyOfferSseConnection;
    private structSseConnection: StructSseConnection;
    private liveEventsCountConnection: LiveEventsCountConnection;

    private dailyOfferObservable: Observable<SseUpdate<OfferEvent[]>>;
    private structObservable: Observable<SseUpdate<Partial<Struct>>>;
    private liveEventsCountObservable: Observable<SseUpdate<number>>;

    private eventDetailSubscriptionMap: Record<number, EventStream> = {};

    private structManager: StructManager;

    public startStreaming(): void {
        this.structSseConnection = new StructSseConnection();
        this.structSseConnection.startStreaming();
        this.structObservable = this.structSseConnection.getObservable();

        this.dailyOfferSseConnection = new DailyOfferSseConnection();
        this.dailyOfferSseConnection.startStreaming();
        this.dailyOfferObservable = this.dailyOfferSseConnection.getObservable();

        this.startLiveEventStreaming();

        this.structManager = StructManager.getInstance();
    }

    public startLiveEventStreaming() {
        this.liveEventsCountConnection = new LiveEventsCountConnection();
        this.liveEventsCountConnection.startStreaming();
        this.liveEventsCountObservable = this.liveEventsCountConnection.getObservable();
    }

    subscribeToAllEventChangesStream(callback: (events: SseUpdate<OfferEvent[]>) => void): Subscription {
        return this.dailyOfferObservable.pipe(
            concatMap(async (update) => {
                if (update !== OutOfSyncUpdate) {
                    /* We don't want to update events subscribed to details because we could override event object
                    with these updates that are not full. We are going to receive an update over details stream anyway
                    and that update also has full event (full odds list). */
                    const eventsNotSubscribedToDetails = update.filter((event) => !this.isSubscribedToDetails(event.id));
                    await this.structManager.denormalizeEvents(eventsNotSubscribedToDetails);
                    return eventsNotSubscribedToDetails;
                } else {
                    return OutOfSyncUpdate;
                }
            }),
        ).subscribe(callback);
    }

    subscribeToStructStream(callback: (struct: SseUpdate<Partial<SseStructure>>) => void) {
        this.structObservable.pipe(
            map((update) => {
                if (update !== OutOfSyncUpdate) {
                    return update;
                } else {
                    return OutOfSyncUpdate;
                }
            }),
            filter(Boolean),
        ).subscribe(callback);
    }

    subscribeToEventStream(eventId: number, callback: (event: SseUpdate<OfferEvent>) => void): Subscription {
        let eventObservable: Observable<SseUpdate<OfferEvent>>;
        if (!this.eventDetailSubscriptionMap[eventId]) {
            const matchDetailsSseConnection = new MatchDetailsSseConnection(eventId);
            this.eventDetailSubscriptionMap[eventId] = {
                connection: matchDetailsSseConnection,
                subscriptionCount: 0,
            };
        }
        if (!this.eventDetailSubscriptionMap[eventId].observable) {
            const connection = this.eventDetailSubscriptionMap[eventId].connection;
            connection.startStreaming();
            eventObservable = connection.getObservable().pipe(
                concatMap(async (update) => {
                    if (update !== OutOfSyncUpdate) {
                        await this.structManager.denormalizeEvent(update);
                        return update;
                    } else {
                        return OutOfSyncUpdate;
                    }
                }),
                share(),
            );
            this.eventDetailSubscriptionMap[eventId].observable = eventObservable;
        }
        this.eventDetailSubscriptionMap[eventId].subscriptionCount += 1;
        eventObservable = this.eventDetailSubscriptionMap[eventId].observable!;
        return eventObservable.subscribe(callback);
    }

    setEventDetailsTimestamp(event: OfferEvent) {
        if (!this.eventDetailSubscriptionMap[event.id]) {
            const matchDetailsSseConnection = new MatchDetailsSseConnection(event.id);
            this.eventDetailSubscriptionMap[event.id] = {
                connection: matchDetailsSseConnection,
                subscriptionCount: 0,
            };
        }
        this.eventDetailSubscriptionMap[event.id].connection.updateMatchDetailsTimestamp(event);
    }

    unsubscribeFromEventStream(eventId: number): void {
        if (!this.eventDetailSubscriptionMap[eventId]) {
            return;
        }
        this.eventDetailSubscriptionMap[eventId].subscriptionCount -= 1;
        if (this.eventDetailSubscriptionMap[eventId].subscriptionCount === 0) {
            this.eventDetailSubscriptionMap[eventId].connection.stopStreaming();
            delete this.eventDetailSubscriptionMap[eventId];
        }
    }

    subscribeToLiveEventsCountChangesStream(callbackFunction: (eventsCount: number) => void): Subscription {
        return this.liveEventsCountObservable.subscribe((update) => callbackFunction(update as number));
    }

    private isSubscribedToDetails(eventId: number): boolean {
        return this.eventDetailSubscriptionMap[eventId]?.observable !== undefined;
    }
}
